import {Container, Header, Image, Sidebar} from "semantic-ui-react";
import {HeaderBar} from "./Header/Header";
import {ItemsSidebar} from "./ItemsSidebar/ItemsSidebar";
import {Canvas} from "./Canvas/Canvas";
import {PropsSidebar} from "./PropsSidebar/PropsSidebar";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {useState, useEffect, useRef} from "react";
import {TransformComponent, TransformWrapper} from "react-zoom-pan-pinch";
import Login from "./Auth/Login";
import Register from "./Auth/Register";
import {Link, RouterProvider, createBrowserRouter} from "react-router-dom";
import {CompanyData} from "./CompanyDataTables/CompanyData";
import {useSelector} from "react-redux";
import {RootState} from "../app/store";
import ChangePass from "./Auth/ChangePass";
import {SetZoomValue} from "./MainActions";
import {useAppDispatch} from "../app/hooks";
import logo from "../Ll.png";
import {useTranslation} from "react-i18next";
import DocumentationPage from "./Documentation/DocumentationPage";
import {GetAllCompanyData} from "./CompanyDataTables/TablesActions";

export const Layout = () => {
  const dispatch = useAppDispatch();
  const [zoom, setZoom] = useState(0); //control box of moveable (hide when change with input): 0 - hidden, 1 - shown
  const [panDisabled, setPanDisabled] = useState(true); //grab cursor - panning event
  const [isPanning, setIsPanning] = useState(false); //track grab action
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const {isAuthenticated, role, companyId} = useSelector((state: RootState) => state.auth);
  const fetchedCompanyIdRef = useRef<number | null>(null);

  useEffect(() => {
    // console.log('[Layout] useEffect - companyId:', companyId, 'cached:', fetchedCompanyIdRef.current);
    if (fetchedCompanyIdRef.current === companyId) {
      // console.log('[Layout] Skipping - already fetched');
      return;
    }
    fetchedCompanyIdRef.current = companyId;
    // console.log('[Layout] Fetching company data');
    dispatch(GetAllCompanyData(companyId));
  }, [dispatch, companyId]);
  const style = {
    pushable: {
      width: "100%",
      height: `calc(100vh - 55px)`,
      overflow: "hidden",
      cursor: isPanning ? "grabbing" : panDisabled ? "" : "grab",
    },
    pusher: {
      height: "100%",
      overflow: "scroll",
    },
  };

  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <div>
          <Register show={showRegister} showLogin={() => setShowLogin(true)} onHide={() => setShowRegister(false)} />
          <Login show={showLogin} showRegister={() => setShowRegister(true)} onHide={() => setShowLogin(false)} />
          <TransformWrapper
            initialScale={1}
            maxScale={10}
            initialPositionX={0}
            initialPositionY={0}
            panning={{disabled: panDisabled, excluded: ["input", "p", "textarea", "img", "moveable-item", panDisabled ? "div" : "hr"]}}
            limitToBounds={false}
            smooth={true}
            pinch={{disabled: true}}
            wheel={{disabled: panDisabled, step: 0.1}}
            doubleClick={{disabled: true, excluded: ["input", "p", "textarea", "img", "div"]}}
            minScale={0.03}
            centerZoomedOut={false}
            onPanning={(e) => {
              setIsPanning(true);
            }}
            onPanningStop={(e) => {
              setIsPanning(false);
            }}
            onWheelStop={(e) => {
              dispatch(SetZoomValue(e.instance.transformState.scale as number));
            }}
          >
            <HeaderBar setZoom={setZoom} setPanDisabled={setPanDisabled} panDisabled={panDisabled} showLogin={() => setShowLogin(true)} />
            <DndProvider backend={HTML5Backend}>
              <Sidebar.Pushable as={Container} style={style.pushable}>
                <ItemsSidebar setZoom={setZoom} />
                <Sidebar.Pusher style={style.pusher}>
                  <TransformComponent>
                    <Canvas zoom={zoom} setZoom={setZoom} setPanDisabled={setPanDisabled} />
                  </TransformComponent>
                </Sidebar.Pusher>
                <PropsSidebar setZoom={setZoom} />
              </Sidebar.Pushable>
            </DndProvider>
          </TransformWrapper>
        </div>
      ),
      errorElement: <EmptyErrorLayout />,
    },
    {
      path: "/company-data",
      element: isAuthenticated && (role === "admin" || role === "super_admin") ? <CompanyData /> : <EmptyErrorLayout />,
      errorElement: <EmptyErrorLayout />,
    },
    {
      path: "/password-token/:token",
      element: <ChangePass />,
      errorElement: <EmptyErrorLayout />,
    },
    {
      path: "/user-manual",
      element: (
        <div>
          <Register show={showRegister} showLogin={() => setShowLogin(true)} onHide={() => setShowRegister(false)} />
          <Login show={showLogin} showRegister={() => setShowRegister(true)} onHide={() => setShowLogin(false)} />
          <DocumentationPage showLogin={() => setShowLogin(true)} />
        </div>
      ),
      errorElement: <EmptyErrorLayout />,
    },
  ]);

  return <RouterProvider router={router} />;
};

export const EmptyErrorLayout = () => {
  const {t} = useTranslation();
  return (
    <>
      <div className="headerContainer" style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
        <div className="header-button-gr" style={{display: "flex", alignItems: "center"}}>
          <Link to="/">
            <Header as="h3">
              <Image src={logo} size="medium" verticalAlign="middle" />
            </Header>
          </Link>
        </div>
      </div>
      <Container style={{marginTop: 60}}>
        <Header as="h1" style={{fontSize: "5rem"}}>
          {t("404_error")}
        </Header>
        <Header as="h2">{t("page_not_found")}</Header>
        <Header as="h4">
          <Link to="/">{t("back_to_home")}</Link>
        </Header>
      </Container>
    </>
  );
};
